import type { RefObject } from 'react';

import { useCallback, useState, useEffect, useRef } from 'react';

type UseVisibleFiltersCalculationProps = {
  enabled?: boolean;
  isMobile: boolean;
  numberOfAppliedFacets: number;
};

type UseVisibleFiltersCalculationResult<T extends HTMLElement> = {
  visibleFiltersCount: number;
  hasOverflow: boolean;
  scrollContainerRef: RefObject<T | null>;
  calculateVisibleFilters: () => void;
};

const isClearAllElement = (element: Element): element is HTMLElement =>
  element instanceof HTMLElement && element.dataset.clearAllPlacement !== undefined;

export const useVisibleFiltersCalculation = <T extends HTMLElement>({
  enabled = true,
  isMobile,
  numberOfAppliedFacets,
}: UseVisibleFiltersCalculationProps): UseVisibleFiltersCalculationResult<T> => {
  const [visibleFiltersCount, setVisibleFiltersCount] = useState<number>(numberOfAppliedFacets);
  const [hasOverflow, setHasOverflow] = useState<boolean>(false);
  const scrollContainerRef = useRef<T>(null);
  const animationFrameRef = useRef<number | null>(null);

  const calculateVisibleFilters = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    animationFrameRef.current = requestAnimationFrame(() => {
      animationFrameRef.current = null;

      if (isMobile || window.innerWidth <= 767) {
        setHasOverflow(false);
        setVisibleFiltersCount(numberOfAppliedFacets);

        return;
      }

      const container = scrollContainerRef.current;

      if (!container || container.children.length === 0) {
        return;
      }

      const containerWidth = container.offsetWidth;

      if (containerWidth === 0) {
        return;
      }

      const clearAllButton = Array.from(container.children)
        .filter(isClearAllElement)
        .find(element => element.offsetWidth > 0);
      const clearAllWidth = clearAllButton ? clearAllButton.offsetWidth + 8 : 0;

      const overflowButtonWidth = 48;
      const availableWidth = containerWidth - clearAllWidth - overflowButtonWidth;

      const filterElements = Array.from(container.children).filter(child => {
        if (isClearAllElement(child)) {
          return false;
        }

        if (child instanceof HTMLElement && child.dataset.overflow === 'true') {
          return false;
        }

        return true;
      }) as HTMLElement[];

      let visibleCount = 0;
      let totalWidth = 0;

      filterElements.every((element, index) => {
        const elementWidth = element.offsetWidth + 8;

        if (totalWidth + elementWidth <= availableWidth) {
          totalWidth += elementWidth;
          visibleCount = index + 1;

          return true;
        }

        return false;
      });

      const hasOverflowState = visibleCount < filterElements.length;

      setVisibleFiltersCount(visibleCount);
      setHasOverflow(hasOverflowState);
    });
  }, [isMobile, numberOfAppliedFacets]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const container = scrollContainerRef.current;

    if (!container) {
      return undefined;
    }

    let isDisposed = false;
    const observedFilterElements = new Set<HTMLElement>();
    const resizeObserver = new ResizeObserver(calculateVisibleFilters);

    const updateObservedFilterElements = () => {
      const currentFilterElements = new Set(
        Array.from(container.children).filter(
          (element): element is HTMLElement => element instanceof HTMLElement
        )
      );

      observedFilterElements.forEach(element => {
        if (!currentFilterElements.has(element)) {
          resizeObserver.unobserve(element);
          observedFilterElements.delete(element);
        }
      });

      currentFilterElements.forEach(element => {
        if (!observedFilterElements.has(element)) {
          resizeObserver.observe(element);
          observedFilterElements.add(element);
        }
      });
    };

    const mutationObserver = new MutationObserver(() => {
      updateObservedFilterElements();
      calculateVisibleFilters();
    });

    resizeObserver.observe(container);
    updateObservedFilterElements();

    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });

    calculateVisibleFilters();

    if (document.fonts) {
      document.fonts.ready.then(() => {
        if (!isDisposed) {
          calculateVisibleFilters();
        }
      });
    }

    return () => {
      isDisposed = true;
      resizeObserver.disconnect();
      mutationObserver.disconnect();

      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [calculateVisibleFilters, enabled]);

  return {
    visibleFiltersCount,
    hasOverflow,
    scrollContainerRef,
    calculateVisibleFilters,
  };
};
